import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { DatePicker } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import SegmentedTabs from '../../reusable/SegmentedTabs/SegmentedTabs';
import StatusChip, { toneFor } from '../../reusable/StatusChip/StatusChip';
import Button from '../../reusable/Button/Button';
import CopyButton from '../../reusable/CopyButton/CopyButton';
import DailyPerformance from '../../reusable/DailyPerformance/DailyPerformance';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import InfoTip from '../../reusable/InfoTip/InfoTip';
import LineChart from '../../reusable/LineChart/LineChart';
import ChartLegend from '../../reusable/ChartLegend/ChartLegend';
import { CHART_COLORS } from '../../reusable/charts/chartTheme';
import { useTopbarSlot } from '../../layout/TopbarSlot';
import ShareDialog from '../../layout/ShareDialog';
import { useGetAccountsQuery } from '../../store/api/dashboardApi';
import { selectAccount, selectPickedAccount } from '../../store/slices/accountSlice';
import {
  riskCounts,
  useGetAccountBreakdownQuery,
  useGetAccountObjectivesQuery,
  useGetAccountInactivityQuery,
  useGetLossLimitAlertQuery,
  useGetBreachDataQuery,
  useGetPerformanceChartQuery,
  useGetStatisticsDefinitionsQuery,
} from '../../store/api/metricsApi';
import { NA, phaseLabel, signedUsd, usd } from '../../utilities/format';
import { formatAccountSize, formatLoginId } from '../../utilities/accounts';
import AdvancedSections, { AdvancedStatistics, StatLabel, toDefinitionMap } from './AdvancedSections';
import AccountAnalysis from './AccountAnalysis/AccountAnalysis';
import AutoRefresh, { readRefreshSeconds } from './AutoRefresh';
import {
  BannerSkeleton,
  CurveSkeleton,
  DailySkeleton,
  InfoSkeleton,
  ObjectivesSkeleton,
  StatsSkeleton,
  TradesSkeleton,
} from './AccountMetricsSkeleton';
import { t } from '../../i18n/i18n';
import './AccountMetrics.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * Auto-refresh for the page's live figures, so a trader watching an open
 * account sees new trades, balance and limits without reloading. Every polled
 * read uses the interval the trader picks in the top bar (10–30s, 30s by
 * default — AutoRefresh.jsx). Paused while the tab isn't in view. Coming back
 * is not a refresh of its own (no `refetchOnFocus`): flipping between tabs
 * would fire every live call each time — see the visibility effect below,
 * which only catches up when the countdown ran out while away.
 */
const liveOptions = (seconds) => ({
  pollingInterval: seconds * 1000,
  skipPollingIfUnfocused: true,
});

/**
 * The Max Risk Rule article — the same one the Help Centre's `max-risk` entry
 * links to. The threshold is per account size, which no endpoint reports, so
 * the panel points at the page that lists them rather than guessing a figure.
 */
const RISK_RULE_HELP =
  'https://help.alphacapitalgroup.uk/en/articles/15888623-what-is-the-max-risk-rule-and-how-does-it-work';

/** "17 Nov 2023", the format the account fields draw. */
/** 100 → "100%", 33.3333 → "33.33%". */
const percent = (value) => `${Number(value.toFixed(2))}%`;

/**
 * A day as the API writes it, without moving it through a timezone (N36). A
 * bare "2026-09-11" parses as UTC midnight, which west of UTC is still the
 * 10th — a best day dated before the account began. The calendar day is read
 * off the string itself and drawn as that day wherever the browser is.
 */
const asDate = (value) => {
  const day = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (day) return format(new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])), 'dd MMM yyyy');
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? format(parsed, 'dd MMM yyyy') : NA;
};

/**
 * "10 Sep 2026, 13:26" from the account record's zone-less stamps
 * (`"2026-09-10 13:26:15.021191"`) — the broker's own clock, so shown as
 * written and labelled broker time, as on Trade History (N41).
 */
const asBrokerTime = (value) => {
  const [date, time] = String(value ?? '').split(/[ T]/);
  const parsed = Date.parse(`${date}T${(time ?? '00:00:00').slice(0, 8)}`);
  return Number.isFinite(parsed)
    ? t('{{time}} broker time', { time: format(parsed, 'dd MMM yyyy, HH:mm') })
    : NA;
};

/**
 * When the account failed, to the second — "10 Sep 2026, 09:54:06" — on the
 * broker's clock, read as written (`breached_at.time`, as Account information's
 * "Breached at" reads it).
 */
const failedWhen = (value) => {
  const [date, time] = String(value ?? '').split(/[ T]/);
  const parsed = Date.parse(`${date}T${(time ?? '00:00:00').slice(0, 8)}`);
  return Number.isFinite(parsed) ? format(parsed, 'd MMM yyyy, HH:mm:ss') : null;
};

const HELP_ARTICLES = 'https://help.alphacapitalgroup.uk/en/articles';

/**
 * The help-centre article for the rule a breach names (Trading Rules
 * collection), matched on the breach's reason or category. Order matters:
 * "max per asset loss" is the Max Risk Rule, not the maximum total loss.
 * Anything unrecognised goes to the collection itself.
 */
const RULE_ARTICLES = [
  [/daily/i, `${HELP_ARTICLES}/6934210-what-are-the-daily-risk-limits-and-how-do-they-work`],
  [/per.?asset|max.?risk/i, `${HELP_ARTICLES}/15888623-what-is-the-max-risk-rule-and-how-does-it-work`],
  [/max.?loss|overall|total.?loss|drawdown|mll/i, `${HELP_ARTICLES}/6934220-what-is-the-maximum-total-loss`],
  [/inactiv/i, `${HELP_ARTICLES}/8420080-inactivity`],
  [/news/i, `${HELP_ARTICLES}/9293522-can-i-trade-news`],
  [/weekend/i, `${HELP_ARTICLES}/6934247-can-i-hold-trades-over-the-weekend`],
  [/duration|2.?min|two.?min/i, `${HELP_ARTICLES}/8447268-what-is-the-2-minute-average-trade-duration-rule`],
  [/gambl/i, `${HELP_ARTICLES}/7060909-what-is-alpha-capital-group-s-policy-against-gambling-in-trading`],
];
const RULES_COLLECTION = 'https://help.alphacapitalgroup.uk/en/collections/19682652-trading-rules';
const ruleArticle = (rule) =>
  RULE_ARTICLES.find(([pattern]) => pattern.test(String(rule ?? '')))?.[1] ?? RULES_COLLECTION;

/**
 * A recent trade's close as "11 Sep, 16:51" (N91 — the time alone left the day
 * to guess). Broker clock, read as written; the year is left off, the list
 * being the newest few.
 */
const tradeWhen = (value) => {
  const [date, time] = String(value ?? '').split(/[ T]/);
  const parsed = Date.parse(`${date}T${(time ?? '00:00:00').slice(0, 8)}`);
  return Number.isFinite(parsed) ? format(parsed, 'dd MMM, HH:mm') : NA;
};

const ratio = (v) => v.toFixed(2);
const money = (v) => usd(v, { decimals: 2 });
const signedMoney = (v) => signedUsd(v, { decimals: 2 });

/**
 * The twelve cells of 1:9320, keyed on the breakdown endpoint's `statistics`.
 * `get` reads a cell whose figure isn't one flat key. Only the P/L figures that
 * the design tints are `signed`; average win, expectancy and largest win stay
 * body white there — AC1-I123.
 */
const STATS = [
  { key: 'total_trades', label: 'Total trades', format: String },
  {
    key: 'wins',
    label: 'Wins / losses',
    get: (s) => (s.wins == null || s.losses == null ? null : `${s.wins} / ${s.losses}`),
    format: String,
  },
  { key: 'net_profit', label: 'Net profit', format: signedMoney, signed: true },
  { key: 'average_win', label: 'Average win', format: money },
  { key: 'average_loss', label: 'Average loss', format: signedMoney, signed: true },
  { key: 'expectancy', label: 'Expectancy', format: money },
  { key: 'largest_win', label: 'Largest win', format: money },
  { key: 'worst_day', label: 'Worst day', get: (s) => s.worst_day?.net_profit, format: signedMoney, signed: true },
  { key: 'max_drawdown', label: 'Max drawdown', format: (v) => `${v.toFixed(2)}%` },
  { key: 'recovery_factor', label: 'Recovery factor', format: ratio },
  { key: 'sharpe_ratio', label: 'Sharpe ratio', format: ratio },
  { key: 'average_hold', label: 'Average hold', format: String },
];

/** The platform slugs the API takes, spelled the way the platforms do. */
const PLATFORM_LABELS = {
  mt5: 'MetaTrader 5',
  mt4: 'MetaTrader 4',
  ctrader: 'cTrader',
  dxtrade: 'DX Trade',
  tradelocker: 'TradeLocker',
};

/** `at_risk` → "at risk", which is how StatusChip keys its tones. */
const statusText = (value) => String(value ?? '').replace(/_/g, ' ') || NA;

/** The account type as a word — "Swap_Free" → "Swap-free", "standard" → "Standard" (F24). */
const readableType = (value) => {
  const text = String(value ?? '').replace(/_/g, ' ').trim().toLowerCase();
  if (!text) return NA;
  if (text === 'swap free') return 'Swap-free';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * The curve's `{ balance, equity, time }[]` → LineChart's `{ x, y }[]`.
 * Equity only: the card draws one curve, so the point's `balance` and the
 * profit that was derived from it (balance over the account's size) are not
 * read here.
 * NEEDS PINNING: assumes the response is the bare array, since `range` is
 * already a request parameter.
 */
const toSeries = (curve) =>
  // Guarded, not coerced: a non-array response reads as no curve, not a crash.
  (Array.isArray(curve) ? curve : [])
    .map((point) => {
      const y = Number(point?.equity);
      // "2026-07-25 00:48:39" → ISO, which Safari also parses.
      const x = String(point?.time ?? '').replace(' ', 'T');
      return Number.isFinite(y) && Number.isFinite(Date.parse(x)) ? { x, y } : null;
    })
    .filter(Boolean);


/** label / value pair in Account information — 1:9547 */
/**
 * `secret` is a credential: it renders as dots until the eye is pressed, so a
 * password can be read off the screen rather than only copied blind. Held in
 * local state and never persisted — it falls back to hidden on every mount, so
 * leaving the page and returning does not leave a password on display.
 */
/**
 * Time to the daily loss's reset, ticking — "14:18:28". Its own component so
 * only this cell re-renders each second, not the page. `deadline` is when the
 * reset falls (ms); at zero it says so until the next objectives read moves
 * the deadline on.
 */
function ResetCountdown({ deadline }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.round((deadline - now) / 1000));
  if (left === 0) return t('Resetting…');
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
}

const Field = ({ label, value, copy, mono, secret }) => {
  const [shown, setShown] = useState(false);
  const hasSecret = secret != null && secret !== '';

  return (
    <div className="am-field">
      <span className="am-field__label">{t(label)}</span>
      <div className="am-field__row">
        <span className={`am-field__value${mono ? ' is-mono' : ''}`}>
          {hasSecret ? (shown ? secret : '••••••••••') : value}
        </span>
        {hasSecret && (
          <button
            type="button"
            className="am-field__reveal"
            onClick={() => setShown((prev) => !prev)}
            aria-pressed={shown}
            aria-label={
              shown
                ? t('Hide {{label}}', { label: t(label).toLowerCase() })
                : t('Show {{label}}', { label: t(label).toLowerCase() })
            }
            title={
              shown
                ? t('Hide {{label}}', { label: t(label).toLowerCase() })
                : t('Show {{label}}', { label: t(label).toLowerCase() })
            }
          >
            {shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          </button>
        )}
        {copy != null && <CopyButton value={copy} label={t(label)} />}
      </div>
    </div>
  );
};

export default function AccountMetrics({ initialMode }) {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const defaultMode =
    initialMode ??
    (tabParam === 'analysis' || tabParam === 'account-analysis'
      ? 'Account Analysis'
      : tabParam === 'advanced'
        ? 'Advanced'
        : 'Simple');
  const [mode, setMode] = useState(defaultMode);
  // The auto-refresh interval, in seconds — the top bar's control sets it.
  const [refreshSeconds, setRefreshSeconds] = useState(readRefreshSeconds);
  const LIVE = liveOptions(refreshSeconds);
  const [range, setRange] = useState('1M');
  /**
   * A free date range for the equity curve (N35), as live's picker gives —
   * `[dayjs, dayjs]` or null. Picking one clears the preset tab, and picking a
   * tab clears it, so the card only ever answers one question.
   */
  const [customRange, setCustomRange] = useState(null);
  const pickPreset = (value) => {
    setCustomRange(null);
    setRange(value);
  };
  const [isShareOpen, setIsShareOpen] = useState(false);

  /**
   * Which account this page is about. The Dashboard's Open button deep-links it
   * — `/account-metrics?login=2826423&platform=mt5` — while the sidebar's own
   * nav item arrives with nothing, so fall back to the first account on the
   * profile. That read is free: the Dashboard's query is already in the cache.
   */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: accountList } = useGetAccountsQuery({ page: 1, page_size: 100 });
  const accounts = accountList?.accounts?.results;
  const firstAccount = accounts?.[0];

  const pickedAccount = useSelector(selectPickedAccount);

  /**
   * A login that isn't one of the trader's falls back to their first account,
   * and the URL is corrected to match. The list is only the first hundred, so
   * a login past them (picked from the account picker's later pages, N22) is
   * looked up by itself before it's judged: found, it's the account; not
   * found, it isn't the trader's.
   */
  const candidateLogin = params.get('login') ?? pickedAccount.loginId;
  const matchedAccount = accounts?.find(
    (row) => String(row.login_id) === String(candidateLogin)
  );
  const listIsWhole =
    accounts != null && (accountList?.accounts?.count ?? accounts.length) <= accounts.length;
  const lookup = useGetAccountsQuery(
    { page: 1, page_size: 100, search: String(candidateLogin) },
    { skip: !candidateLogin || !accounts || Boolean(matchedAccount) || listIsWhole },
  );
  const lookedUp = lookup.data?.accounts?.results?.find(
    (row) => String(row.login_id) === String(candidateLogin),
  );
  /** The account the address names, from the list or the lookup — its own record. */
  const knownAccount = matchedAccount ?? lookedUp;
  const loginId = knownAccount
    ? String(knownAccount.login_id)
    : candidateLogin && !listIsWhole && !lookup.isSuccess
      ? String(candidateLogin)
      : firstAccount
        ? String(firstAccount.login_id)
        : null;
  const activeAccount =
    knownAccount ?? (String(firstAccount?.login_id) === loginId ? firstAccount : null);
  // The account's own record before the address (N114): a cTrader account
  // opened with `platform=mt5` was asking the MT5 ledger for it and drawing
  // N/A everywhere. The address only decides while the record is on its way.
  const rawPlatform =
    activeAccount?.platform ??
    params.get('platform') ??
    (String(pickedAccount.loginId) === String(loginId) ? pickedAccount.platform : null) ??
    firstAccount?.platform ??
    null;
  const platform = rawPlatform ? String(rawPlatform) : undefined;

  useEffect(() => {
    if (
      loginId &&
      (String(pickedAccount.loginId) !== String(loginId) ||
        (platform && pickedAccount.platform !== platform))
    ) {
      dispatch(selectAccount({ loginId, platform }));
    }
  }, [loginId, platform, pickedAccount.loginId, pickedAccount.platform, dispatch]);

  useEffect(() => {
    // A bare path, or a login that fell back above: write what was resolved.
    if (
      loginId &&
      (params.get('login') !== loginId || (platform && params.get('platform') !== platform))
    ) {
      const next = new URLSearchParams(params);
      next.set('login', loginId);
      if (platform) next.set('platform', platform);
      setParams(next, { replace: true });
    }
  }, [loginId, platform, params, setParams]);

  /**
   * Where "View all" goes. Built from the resolved account rather than from
   * `params`, which is empty on the first render before the sync effect above
   * writes the login back into the URL.
   */
  const tradeHistoryHref = (() => {
    if (!loginId) return '/trade-history';
    const next = new URLSearchParams({ login: String(loginId) });
    if (platform) next.set('platform', platform);
    return `/trade-history?${next}`;
  })();

  /**
   * The account's record, its recent trades and its statistics — and, on
   * Advanced, the sections below Account information. The top bar's toggle is
   * this call's `category`; both categories carry the Simple fields, so one
   * request per toggle feeds the whole page. Switching keeps the last
   * category's `data` on screen until the other lands, so the cards above
   * don't drop back to skeletons.
   */
  const metrics = useGetAccountBreakdownQuery(
    { loginId, platform, category: mode.toLowerCase() },
    // Auto-refreshed (review: "so the client doesn't need to refresh each
    // time"): new trades, balance and statistics arrive on their own.
    { skip: !loginId, refetchOnMountOrArgChange: true, ...LIVE },
  );

  const statisticsCategory = mode.toLowerCase() === 'advanced' ? 'advanced' : 'simple';
  // What each statistic means, in the backend's words — the (i) beside every
  // label in the Statistics card, Simple's twelve and Advanced's ledger alike.
  const statisticsDefinitions = useGetStatisticsDefinitionsQuery(statisticsCategory).data;
  const statDefs = useMemo(() => toDefinitionMap(statisticsDefinitions), [statisticsDefinitions]);

  const account = metrics.data?.account_information;
  const challenge = account?.challenge;

  const riskStatus = metrics.data?.risk_status;

  /**
   * The account's breach record, as the live Account Metric fetches it
   * (`fetchBreachData`). It decides whether the Risk status bar — the way into
   * Breach Analysis — shows at all: see `showRisk` below.
   */
  const breach = useGetBreachDataQuery(loginId, { skip: !loginId, ...LIVE });

  /** `account_information` has no `stage`; the flashcard does. */
  const stage = phaseLabel(metrics.data?.flashcard?.stage) ?? NA;

  /**
   * A competition account — N109. Its record still says "Evaluation" (and the
   * flashcard's stage does too), and it carries the plan's `profit_target`, but
   * the live Account Metric reads `is_competition` first: the stage is
   * "Competition", and neither the profit target nor the minimum trading days
   * applies — a competition is ranked on its result, not passed.
   */
  const isCompetition = Boolean(account?.is_competition);

  /**
   * The stage as the live dashboard writes it — "Competition (Steps 2)",
   * "Evaluation (Steps 2)": the competition word where it applies, then the
   * challenge's step count.
   */
  const stepCount = Number(challenge?.steps);
  const stageLabel =
    stepCount > 0
      ? t('{{stage}} (Steps {{count}})', {
          stage: t(isCompetition ? 'Competition' : stage),
          count: stepCount,
        })
      : t(isCompetition ? 'Competition' : stage);

  /**
   * The banner's facts — N86 — as separate pills rather than one dot-joined
   * line. "MetaTrader 5 · Evaluation · Phase 01 (Steps 1)" ran three
   * overlapping names for the same account together and read as noise; apart,
   * each is legible on its own and the line stops looking like a sentence that
   * repeats itself.
   *
   * The stage shown is the one the Dashboard's Stage column uses — the account
   * list row's `stage`, "Phase 01" while evaluating, "Qualified" once funded —
   * because the flashcard's is the account's type again.
   *
   * Deduped across the whole list, not just neighbours: the repeat it is there
   * to catch ("Evaluation" as both type and stage) is not always adjacent once
   * a part in between has been dropped.
   */
  const bannerFacts = [
    platform ? (PLATFORM_LABELS[platform] ?? platform) : null,
    /*
     * `account_information.status` is deliberately not listed. It is the
     * record's own word for the same thing the stage below says — "Funded"
     * beside "Qualified", "Evaluation" beside "Phase 01" — so the pair read as
     * two facts when they are one, and the stage is the one the Dashboard's
     * Stage column agrees with.
     */
    isCompetition ? 'Competition' : knownAccount?.stage ?? stage,
    // Only where it says something: "1-step" beside "Phase 01" does not, and
    // "(Steps 1)" was the most literal part of the old line.
    stepCount > 1 ? t('{{count}}-step', { count: stepCount }) : null,
  ]
    .map((part) => (part == null ? null : String(part).trim()))
    .filter((part) => part && part !== NA)
    .filter(
      (part, i, parts) =>
        parts.findIndex((other) => other.toLowerCase() === part.toLowerCase()) === i,
    );

  /**
   * N110 — the account fields the live Account Metric shows and this page did
   * not, from the same response. `breached_at` and `daily_max_dd` arrive as
   * JSON strings: the balance, equity and time at the breach, and the day's
   * opening (rollover) balance and equity.
   */
  const parseJson = (value) => {
    if (value && typeof value === 'object') return value;
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  };
  const breachedAt = parseJson(account?.breached_at);
  const rollover = parseJson(account?.daily_max_dd);
  const flashcard = metrics.data?.flashcard;
  const swapPaid =
    metrics.data?.statistics?.swap_paid == null ? null : Number(metrics.data.statistics.swap_paid);

  /**
   * The account's title. A competition account's `name` is its owner's email
   * address (N109); its `nickname` ("COMP-$100K-231123") is the account's
   * own name, so an email-shaped `name` gives way to it.
   */
  const accountName = /@/.test(account?.name ?? '')
    ? account?.nickname || account?.name
    : account?.name || account?.nickname;

  /**
   * The account's own state, "active" → "Active" — AC1-I121. Not
   * `account_information.progress`: that's the objectives' "In Progress",
   * which the Trading objectives chip already shows.
   */
  const accountStatus = statusText(metrics.data?.flashcard?.status ?? '').replace(/^\w/, (c) =>
    c.toUpperCase()
  );

  /** The objectives card's own source — always fresh, polls for live trading updates. */
  const objectives = useGetAccountObjectivesQuery(
    { loginId, platform },
    {
      skip: !loginId,
      refetchOnMountOrArgChange: true,
      ...LIVE,
    }
  );

  /**
   * `/v2/inactivity/<login>/` — the account's inactivity state, for the same
   * login and platform as everything else on the page. Fetched once per
   * account (cached when you come back to it): it moves by the day, not the
   * second, so it isn't polled with the objectives.
   */
  const inactivity = useGetAccountInactivityQuery({ loginId, platform }, { skip: !loginId });

  /**
   * `/v2/account/<login>/loss-limit-alert/` — the warning before a loss limit
   * is reached, for the same login and platform as the rest of the page.
   * `{ loss_limit_alert: { used_pct, triggered, remaining, limit } }`: the
   * strip under the banner shows only once the backend says `triggered`.
   */
  const lossLimitAlert = useGetLossLimitAlertQuery({ loginId, platform }, { skip: !loginId, ...LIVE });
  const lossAlert = lossLimitAlert.currentData?.loss_limit_alert;
  const showLossAlert = lossAlert?.triggered === true;

  /**
   * The range tabs are 1D/1W/1M/3M/ALL — the values the API takes, lowercased.
   * A custom range asks for ALL and narrows it here, as live does: the API has
   * no from/to of its own.
   */
  const curve = useGetPerformanceChartQuery(
    { loginId, platform, range: customRange ? 'all' : range.toLowerCase() },
    { skip: !loginId, ...LIVE },
  );

  /*
   * Back on the tab: refresh once, and only if the countdown ran out while it
   * was hidden. Back before then, nothing is asked and the count carries on —
   * so flipping between tabs costs at most one refresh per interval, the same
   * as staying on the page. The latest queries sit in a ref, so the listener
   * is attached once.
   */
  const live = useRef(null);
  live.current = {
    queries: [metrics, breach, objectives, lossLimitAlert, curve],
    last: metrics.fulfilledTimeStamp,
    seconds: refreshSeconds,
  };
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const { queries, last, seconds } = live.current;
      if (last && Date.now() - last < seconds * 1000) return;
      queries.forEach((query) => {
        if (!query.isUninitialized && !query.isFetching) query.refetch();
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Loading only when there is nothing for these arguments yet — a new range
  // or account. A background refresh keeps the curve on screen instead of
  // flashing the loading state every half-minute.
  const isCurveLoading = curve.isLoading || (curve.isFetching && !curve.currentData);

  /** Closed trades, newest first, as the API sends them. */
  const trades = metrics.data?.recent_trades ?? [];

  /** The calendar opens on the last day in `day_wise_stats`. */
  const dayStats = metrics.data?.day_wise_stats ?? [];
  const tradeMonth = dayStats.length ? new Date(dayStats[dayStats.length - 1].date) : null;

  const points = useMemo(() => {
    const series = toSeries(curve.data);
    if (!customRange) return series;
    // Whole days, both ends included — the times are the broker's clock, so
    // they're compared as written rather than shifted through a timezone.
    const from = customRange[0].format('YYYY-MM-DD');
    const to = customRange[1].format('YYYY-MM-DD');
    return series.filter((point) => {
      const day = point.x.slice(0, 10);
      return day >= from && day <= to;
    });
  }, [curve.data, customRange]);

  /** Last value on the curve, and how far it has travelled across the window. */
  const latest = points.length ? points[points.length - 1].y : null;
  const change = points.length > 1 ? latest - points[0].y : null;
  const changePct = change == null || !points[0]?.y ? null : (change / points[0].y) * 100;

  // The curve has no data yet — say which of the reasons it is.
  const curveNote = !loginId
    ? t('Open an account from the Dashboard to see its curve.')
    : curve.isError
      ? t("Could not load this account's equity ({{status}}).", { status: curve.error?.status ?? 'error' })
      : !isCurveLoading && points.length === 0
        ? customRange
          ? t('No equity data in this date range.')
          : t('No equity data came back for this account.')
        : null;

  /**
   * The objectives payload is flat and one metric spans several keys:
   * `max_loss` used, `max_loss_target` allowed, `remaining_max_loss` left.
   */
  const isObjectivesLoading =
    objectives.isLoading || (!objectives.currentData && objectives.isFetching);
  const obj = objectives.currentData;
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

  /** Profit made is the payload's own `profit`; `profit_target` is null when funded. */
  const profitMade = num(obj?.profit);
  const profitTarget = Number(obj?.profit_target) || null;
  const passedPct =
    profitTarget && profitMade != null
      ? Math.max(0, Math.min((profitMade / profitTarget) * 100, 100))
      : null;
  /** Server-computed, so a target the trader has overshot still reads as 0 left. */
  const profitLeft = !profitTarget
    ? null
    : num(obj?.remaining_profit) ?? Math.max(profitTarget - (profitMade ?? 0), 0);
  /**
   * Whether the card draws a profit target at all. A competition account is
   * ranked rather than targeted, and a qualified (funded) one has no target
   * left to hit — it used to show the ring reading N/A beside "No profit
   * target on this account". Neither draws it.
   */
  const hasTarget = !isCompetition && Boolean(profitTarget);

  /** "2026-09-19 03:01:00.796175+00:00" → "03:01", already UTC at that offset. */
  const resetAt = String(obj?.max_daily_loss_reset_utc_time ?? '').slice(11, 16);
  /**
   * When the daily loss resets, from `max_daily_loss_reset_in_seconds` counted
   * from the moment this objectives read arrived — so the countdown stays true
   * however long the page has been open since. Null without the field, and
   * the row falls back to the reset's clock time.
   */
  const resetDeadline =
    obj?.max_daily_loss_reset_in_seconds != null &&
    num(obj.max_daily_loss_reset_in_seconds) != null &&
    objectives.fulfilledTimeStamp
      ? objectives.fulfilledTimeStamp + num(obj.max_daily_loss_reset_in_seconds) * 1000
      : null;

  /**
   * Whether the backend has already called this account over — AC1-I107. Read
   * through `toneFor`, the same table the chip beside it uses, so the rails and
   * the chip can never disagree: "failed", "breached" and "hard breach" all
   * resolve to danger there, and a new failure word only has to be added once.
   */
  // The account's `progress` only, as the live Account Metric checks it
  // (`progress === "Failed"`): `overall_status` can still read in progress
  // after a breach has failed the account, and hid the Risk status bar.
  const accountFailed = toneFor(statusText(account?.progress)) === 'danger';

  /**
   * The Risk status bar and its "View all analytics" show only where the live
   * Account Metric shows its "Breach Analysis →" link: a failed account the
   * breach endpoint has a record for. An account in progress, or one that
   * failed with no breach on record ("Account is Active."), has no breach to
   * open, so the bar would lead to an empty page.
   */
  const showRisk = accountFailed && Boolean(breach.data?.data);

  /** The breach by name — `max_per_asset_loss_breach` → "Max per asset loss breach". */
  const breachReason = (() => {
    const text = String(
      breach.data?.data?.meta_data?.reason ?? breach.data?.data?.category ?? 'rule breach',
    ).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  })();

  /**
   * The failed-account banner (review doc, breach analysis: "Say why the
   * account failed where the trader looks first"): the rule, when, and that
   * rule's help article. The time is the breach snapshot's, then the
   * objectives' `breached_at`, then when the breach was recorded. The link is
   * the backend's own `rule_info_url` (objectives-flat) when it sends one,
   * else the article matched from the rule's name.
   */
  const failedAt = failedWhen(
    breachedAt?.time ?? obj?.breached_at ?? breach.data?.data?.created_at,
  );
  const failedRuleLink =
    obj?.rule_info_url ||
    ruleArticle(breach.data?.data?.meta_data?.reason ?? breach.data?.data?.category);

  /** Used against allowed, for the two loss rails — 1:9268. */
  // Trailing-drawdown accounts carry the overall limit as `mll` instead.
  const trailing = !(Number(obj?.max_loss_target) > 0);

  /**
   * The trailing limit's high-water mark — N111. Only a trailing account has
   * one; it is the highest balance the limit is measured down from.
   */
  const highWaterMark =
    trailing && num(obj?.mll_highest_balance_watermark) > 0
      ? num(obj.mll_highest_balance_watermark)
      : null;
  const limits = [
    {
      label: 'Max daily loss',
      used: num(obj?.max_daily_loss),
      allowed: num(obj?.max_daily_loss_target),
      left: num(obj?.remaining_daily_max_loss),
      status: obj?.max_daily_loss_status,
      // The note rows under the rail, label and value. Start of day and the
      // level the account fails at are always listed on the daily limit, with
      // a dash until the figure arrives.
      rows: [
        // A live countdown when the backend sends the seconds left.
        [
          'Resets',
          resetDeadline ? <ResetCountdown deadline={resetDeadline} /> : `${resetAt || '00:00'} UTC`,
        ],
        [
          'Start of day',
          num(obj?.start_of_day_balance ?? obj?.start_of_day) != null
            ? usd(obj.start_of_day_balance ?? obj.start_of_day, { decimals: 2 })
            : '—',
        ],
        // Only a real level, as the backend sends it. A failed account's
        // threshold comes back as 0, which read "Account fails at $0.00" —
        // the line is left off then rather than shown.
        ...(num(obj?.max_daily_loss_threshold) > 0
          ? [['Account fails at', usd(obj.max_daily_loss_threshold, { decimals: 2 })]]
          : []),
      ],
    },
    {
      key: 'overall',
      label: 'Max overall loss',
      used: num(trailing ? obj?.mll : obj?.max_loss),
      allowed: num(trailing ? obj?.mll_target : obj?.max_loss_target),
      left: num(trailing ? obj?.remaining_mll : obj?.remaining_max_loss),
      // A trailing limit is measured from the account's highest balance, not
      // its start — N111: the high-water mark and the floor it sets, as the
      // live dashboard gives them (`mll_highest_balance_watermark`,
      // `mll_threshold`).
      rows:
        trailing && num(obj?.mll_highest_balance_watermark) > 0
          ? [
              ['High-water mark', usd(obj.mll_highest_balance_watermark, { decimals: 2 })],
              ...(num(obj?.mll_threshold) > 0
                ? [['Floor', usd(obj.mll_threshold, { decimals: 2 })]]
                : []),
            ]
          : account?.start_date
            ? [['Since', asDate(account.start_date)]]
            : [],
      status: trailing ? obj?.mll_status : obj?.max_loss_status,
    },
  ];

  /**
   * The equity curve's target and limit lines — N35, the three live's balance
   * chart draws, from the account size and the objectives' own targets:
   *   profit target  = size + `profit_target` (none once funded)
   *   max loss limit = size − `max_loss_target`; a trailing account's floor
   *                    is `mll_threshold`, which moves with its high-water mark
   *   max daily loss = size − `max_daily_loss_target`
   */
  const accountSize = num(challenge?.account_balance);
  const maxLossFloor = !accountSize
    ? null
    : trailing
      ? num(obj?.mll_threshold) > 0
        ? num(obj.mll_threshold)
        : num(obj?.mll_target) > 0
          ? accountSize - num(obj.mll_target)
          : null
      : num(obj?.max_loss_target) > 0
        ? accountSize - num(obj.max_loss_target)
        : null;
  const dailyLossFloor =
    accountSize && num(obj?.max_daily_loss_target) > 0
      ? accountSize - num(obj.max_daily_loss_target)
      : null;
  // Memoised on the three numbers, so the chart's geometry isn't rebuilt on
  // every 15s poll of the account.
  const curveLevels = useMemo(
    () =>
      [
        accountSize && profitTarget && {
          label: t('Profit target'),
          value: accountSize + profitTarget,
          color: CHART_COLORS.up,
        },
        maxLossFloor != null && { label: t('Max loss limit'), value: maxLossFloor, color: CHART_COLORS.down },
        dailyLossFloor != null && { label: t('Max daily loss'), value: dailyLossFloor, color: CHART_COLORS.warning },
      ].filter(Boolean),
    [accountSize, profitTarget, maxLossFloor, dailyLossFloor],
  );

  /**
   * Consistency — AC1-I117. `consistency` is the best day's share of the
   * profit, in percent; `maximum_consistency` the most it may be. Accounts
   * without the rule send no cap, and don't get the rail.
   */
  const consistencyCap = num(obj?.maximum_consistency);
  const consistency = consistencyCap > 0 && {
    share: num(obj?.consistency),
    cap: consistencyCap,
    bestDay: num(obj?.highest_profit),
    bestDayOn: obj?.highest_profit_day,
  };

  /**
   * The Max Risk Rule — the "1/2/3%" cap clients ask about: open drawdown on
   * any single asset reaching the threshold for the account size closes the
   * account (Help Centre, `max-risk`).
   *
   * The objectives payload now names it — `max_asset_loss`:
   *
   *   { threshold_per: 1, limit_: 50, status: "In Progress" }
   *
   * `threshold_per` is a percentage already (1 → "1%"), not the fraction the
   * breach record sends, and `limit_` is that percentage of the account in
   * dollars — the amount a single asset may be down before the rule closes the
   * account. The breach record stays as the fallback for an account whose
   * objectives carry no block, and the published wording as the last resort.
   *
   * Deliberately no rail. Nothing reports how much of the per-asset allowance
   * has been used while an account is trading, so an empty bar would read as
   * "none used" rather than "not measured".
   */
  const assetLoss = obj?.max_asset_loss;
  const breachRule = breach.data?.data?.meta_data?.breach_metadata;

  /*
   * Whether the breach on record is this rule's (`max_per_asset_loss_breach`),
   * and the loss it was called on — the engine's own figure first, as Breach
   * Analysis reads it. The objectives payload knows nothing of it: its
   * breached_* flags stay 0 for a per-asset breach (F32).
   */
  const breachMeta = breach.data?.data?.meta_data;
  const assetBreached =
    Boolean(breach.data?.data) &&
    /per.?asset|max.?risk/i.test(String(breachMeta?.reason ?? breach.data?.data?.category ?? ''));
  const assetBreachLoss = assetBreached
    ? num(
        breachRule?.calculated_loss ??
          breachMeta?.calculation_breakdown?.total_loss ??
          breachMeta?.breach_loss,
      )
    : null;

  // Percent first from the objectives (already a percentage), then from the
  // breach record (a fraction, so scaled).
  const riskRulePercent =
    num(assetLoss?.threshold_per) ??
    (num(breachRule?.rule_threshold_percent) != null
      ? num(breachRule.rule_threshold_percent) * 100
      : null);

  const riskRuleAmount =
    num(assetLoss?.limit_) ??
    num(breachRule?.rule_threshold_amount) ??
    (riskRulePercent != null && accountSize ? (accountSize * riskRulePercent) / 100 : null);

  /*
   * `limit_` carries no unit and is not documented, so it is checked rather
   * than assumed: on the accounts seen it is `threshold_per` of the account
   * size in dollars (1% of $5,000 = $50), and it is only written as money when
   * that arithmetic still holds. Where it does not, the figure is shown as
   * itself — a wrong unit on a risk limit is worse than a bare number.
   */
  const expectedRiskAmount =
    riskRulePercent != null && accountSize ? (accountSize * riskRulePercent) / 100 : null;
  const riskAmountIsMoney =
    riskRuleAmount != null &&
    (expectedRiskAmount == null ||
      // A point either way, for rounding on the server's side.
      Math.abs(riskRuleAmount - expectedRiskAmount) <= Math.max(1, expectedRiskAmount * 0.01));

  /*
   * When it bites: on a Qualified account, and on Alpha Direct from the first
   * trade. Saying so matters — on an evaluation account that is not Alpha
   * Direct the rule is real but not yet live, and listing it flat would be its
   * own kind of misleading.
   */
  const planName = String(challenge?.plan_type ?? '');
  const isAlphaDirect = /alpha[\s-]*direct/i.test(planName);
  const isQualifiedStage = /qualified|funded/i.test(
    String(knownAccount?.stage ?? stage ?? ''),
  );
  const riskRule = {
    label: riskRulePercent != null ? `${+riskRulePercent.toFixed(2)}%` : '1–3%',
    amount: riskRuleAmount,
    /*
     * A breached account reports this rule as "In Progress" — the engine stops
     * tracking it once the account is closed, so its last word is the one from
     * before the breach. Shown as Failed instead, to agree with the account's
     * own status and with the loss rails above it, which already resolve to
     * danger on `accountFailed`.
     */
    status: accountFailed
      ? 'Failed'
      : assetLoss?.status
        ? statusText(assetLoss.status)
        : null,
    /*
     * Split around the figure so the amount itself can be lit while the words
     * around it stay muted — it is the one number in this row a trader needs
     * to carry away, and in one flat string it read as part of the sentence.
     * `null` where there is no figure, and `note` carries the wording instead.
     */
    limitParts:
      riskRuleAmount == null
        ? null
        : riskAmountIsMoney
          ? {
              lead: 'Max',
              figure: usd(riskRuleAmount, { decimals: 2 }),
              tail: 'open loss on any one asset',
            }
          : {
              lead: 'Limit',
              figure: riskRuleAmount.toLocaleString('en-US'),
              tail: 'on any one asset',
            },
    note: isAlphaDirect
      ? 'Open drawdown on any one asset, from your first trade'
      : isQualifiedStage
        ? 'Open drawdown on any one asset closes the account'
        : 'Applies once this account is Qualified',
    /** The loss the account failed on against the limit — "$59.15 of $50.00". */
    breachNote:
      assetBreachLoss != null && riskRuleAmount != null && riskAmountIsMoney
        ? t('{{used}} of {{allowed}}', {
            used: usd(assetBreachLoss, { decimals: 2 }),
            allowed: usd(riskRuleAmount, { decimals: 2 }),
          })
        : null,
  };

  /**
   * The inactivity rule as a count-down (review doc, metrics: "Count down the
   * inactivity and expiry rules") — days without a trade out of the limit,
   * from `/v2/inactivity/<login>/`'s `inactivity` block, so nobody is caught
   * out by it. `applies: false` (a failed account, say) keeps the tile but
   * says so in its status rather than counting toward a breach.
   */
  const inact = inactivity.currentData?.inactivity;
  const inactivityRule =
    inact && Number(inact.limit_days) > 0
      ? {
          used: Math.max(0, Number(inact.days_without_trade) || 0),
          limit: Number(inact.limit_days),
          remaining: num(inact.days_remaining),
          status: inact.status,
          breached: Boolean(inact.is_breached),
          applies: inact.applies !== false,
          // A breached or expired account reads "Not Applicable": the count
          // stopped meaning anything, so it isn't shown (N112).
          notApplicable: inact.applies === false || /not.?applicable/i.test(String(inact.status ?? '')),
          deadline: inact.deadline,
          lastTrade: inact.last_trade_time,
        }
      : null;

  /**
   * The figures that fill the banner. The strip carried the account number and
   * nothing else, leaving most of its width as bare gradient — these are the
   * three numbers the page is about, so they are legible before a single card
   * has loaded.
   *
   * Taken from the flashcard, which is the same block the hero reads, and P&L
   * is `balance - account size` — the same derivation the equity curve's own
   * headline uses, so the two can never disagree a few hundred pixels apart.
   *
   * Declared here rather than beside `bannerFacts` because `accountSize` is
   * resolved further down.
   */
  const bannerBalance = num(flashcard?.balance);
  const bannerEquity = num(flashcard?.equity);
  const bannerPnl =
    bannerBalance != null && accountSize ? bannerBalance - accountSize : null;
  const bannerStats = [
    { label: 'Balance', value: bannerBalance == null ? NA : money(bannerBalance) },
    { label: 'Equity', value: bannerEquity == null ? NA : money(bannerEquity) },
    {
      label: 'Total P&L',
      value: bannerPnl == null ? NA : signedMoney(bannerPnl),
      // Only where there is a direction to show: flat is neither.
      tone: bannerPnl ? (bannerPnl < 0 ? 'down' : 'up') : null,
    },
  ];

  /**
   * Advanced's objective details under the rails — AC1-I122. Everything comes
   * off the objectives payload and `risk_status`.
   *
   * Only the account's own objectives (N112): a rule the account doesn't have
   * (no consistency cap, no minimum of trading days, no profit target) is left
   * out rather than listed as 0 or "No … rule", and lot discipline and the
   * news filter — which no endpoint reports for any account — aren't listed.
   */
  const ddUsed = ({ used, allowed }) => {
    if (used == null || !(allowed > 0)) return { value: NA, note: NA };
    // A day in profit sends `used` negative; no drawdown has been used then.
    const spent = Math.max(0, used);
    return {
      value: `${Math.round((spent / allowed) * 100)}%`,
      note: t('{{used}} of {{allowed}}', {
        used: usd(spent, { decimals: 2 }),
        allowed: usd(allowed, { decimals: 2 }),
      }),
    };
  };
  const tradingDays = num(obj?.trading_days);
  const tradingDaysTarget = num(obj?.trading_days_target);
  const { warnings, hardBreaches } = riskCounts(riskStatus);
  const consistencyShare = num(obj?.consistency);
  const objectiveDetails = [
    {
      label: 'Consistency',
      applies: Boolean(consistency),
      value: consistencyShare == null ? NA : percent(consistencyShare),
      note: consistency ? t('No single day over {{cap}}', { cap: percent(consistency.cap) }) : NA,
    },
    {
      label: 'Trading days',
      applies: tradingDaysTarget > 0,
      value: tradingDays ?? NA,
      note: tradingDays >= tradingDaysTarget
          ? t('Minimum of {{target}} reached', { target: tradingDaysTarget })
          : t('{{count}} more to reach {{target}}', {
              count: num(obj?.remaining_trading_days) ?? tradingDaysTarget - (tradingDays ?? 0),
              target: tradingDaysTarget,
            }),
    },
    {
      label: 'Rule breaches',
      value: warnings == null || hardBreaches == null ? NA : warnings + hardBreaches,
      note:
        warnings == null || hardBreaches == null
          ? NA
          : `${
              warnings === 1
                ? t('{{count}} warning', { count: warnings })
                : t('{{count}} warnings', { count: warnings })
            }, ${
              hardBreaches === 1
                ? t('{{count}} hard breach', { count: hardBreaches })
                : t('{{count}} hard breaches', { count: hardBreaches })
            }`,
    },
    { label: 'Daily DD used', ...ddUsed(limits[0]) },
    { label: 'Overall DD used', ...ddUsed(limits[1]) },
    // The same rule as Simple's card, with the loss it failed on when it did.
    {
      label: 'Max risk percent',
      value: riskRule.status ? t(riskRule.status) : t('{{limit}} per asset', { limit: riskRule.label }),
      note:
        riskRule.breachNote ??
        (riskRule.limitParts
          ? `${t(riskRule.limitParts.lead)} ${riskRule.limitParts.figure} ${t(riskRule.limitParts.tail)}`
          : t(riskRule.note)),
    },
    {
      label: 'Inactivity',
      applies: Boolean(inactivityRule),
      value: !inactivityRule
        ? NA
        : inactivityRule.notApplicable
          ? t(statusText(inactivityRule.status ?? 'Not Applicable'))
          : `${inactivityRule.used} / ${inactivityRule.limit}`,
      note: !inactivityRule || inactivityRule.notApplicable
        ? t("This rule doesn't apply to the account now.")
        : inactivityRule.breached
          ? t('No trade within the limit')
          : (inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used) === 1
            ? t('Trade within {{count}} day', {
                count: inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used,
              })
            : t('Trade within {{count}} days', {
                count: inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used,
              }),
    },
    {
      label: 'Profit target',
      applies: Boolean(profitTarget),
      value: passedPct == null ? NA : `${Math.round(passedPct)}%`,
      note:
        profitMade == null
          ? NA
          : t('{{used}} of {{allowed}}', {
              used: usd(profitMade, { decimals: 2 }),
              allowed: usd(profitTarget, { decimals: 2 }),
            }),
    },
  ]
    .filter((d) => d.applies !== false)
    // A competition has neither a profit target nor a minimum of trading days
    // (N109) — the live Account Metric drops both for `is_competition`.
    .filter((d) => !isCompetition || !['Profit target', 'Trading days'].includes(d.label));

  /** `{}` when the account hasn't traded, which reads the same as no statistics. */
  const statistics =
    metrics.data?.statistics && Object.keys(metrics.data.statistics).length
      ? metrics.data.statistics
      : null;

  // The Simple/Advanced tablist lives in the top bar in the design — 1:9022.
  useTopbarSlot(
    useCallback(
      () => (
        <div className="am-topbar-controls">
        {/* How often the page refreshes itself — 10 to 30 seconds. */}
        <AutoRefresh
          seconds={refreshSeconds}
          onChange={setRefreshSeconds}
          // The countdown runs from when the page's main read last arrived.
          lastRefreshedAt={metrics.fulfilledTimeStamp}
        />
        <SegmentedTabs
          size="lg"
          radius="tab-track"
          options={['Simple', 'Advanced']}
          value={mode}
          onChange={(nextMode) => {
            setMode(nextMode);
            const next = new URLSearchParams(params);
            if (nextMode === 'Advanced') {
              next.set('tab', 'advanced');
            } else {
              next.delete('tab');
            }
            setParams(next, { replace: true });
          }}
        />
        </div>
      ),
      [mode, params, setParams, refreshSeconds, metrics.fulfilledTimeStamp]
    ),
    [mode, params, setParams, refreshSeconds, metrics.fulfilledTimeStamp]
  );

  /** One loss limit's card — the rail, what's left and its note rows. */
  const renderLimit = (l) => {
    const known = Number.isFinite(l.used) && Number.isFinite(l.allowed) && l.allowed > 0;
    /**
     * A day in profit sends `used` negative (-100.57 against 3,000,
     * so 3,100.57 left) — AC1-I116. Floored at 0: a negative width is
     * invalid CSS, which the block-level fill answers by going full.
     */
    const usedPct = known ? Math.max(0, Math.min((l.used / l.allowed) * 100, 100)) : 0;
    /**
     * Red once this limit's own allowance is gone, and on an account
     * the backend has already failed — a green rail under a "Failed"
     * chip reads as room still left. `accountFailed` is what catches
     * the breach whose figure has since rolled over: the daily loss
     * resets at 00:00 UTC, so the number that ended the account is
     * back near zero by the time most people look at it.
     */
    const breached = known && (l.used >= l.allowed || accountFailed);
    // What's left, as the server reports it — no "Account failed" stand-in
    // on a failed account any more; the status chip and the red rail say that.
    const leftText = !known
      ? NA
      : t('{{amount}} left', { amount: usd(l.left ?? l.allowed - l.used, { decimals: 2 }) });
    return (
      <div
        className={`am-limit${breached ? ' am-limit--breached' : ''}`}
        key={l.label}
      >
        <div className="am-limit__head">
          <span className="am-limit__label">
            {t(l.label)}
            {/* The objective's own status, as the live Account
                Metric gives each one (N110). */}
            {l.status && <StatusChip>{statusText(l.status)}</StatusChip>}
          </span>
          <span className="am-limit__left">{leftText}</span>
        </div>
        <span className="am-limit__rail">
          <span className="am-limit__fill" style={{ width: `${usedPct}%` }} />
        </span>
        <dl className="am-limit__notes am-limit__notes--pairs">
          {[['Used', known ? t('{{used}} of {{allowed}}', { used: usd(l.used), allowed: usd(l.allowed) }) : NA], ...l.rows].map(
            ([key, value]) => (
              <div className="am-limit__row" key={key}>
                <dt>{t(key)}</dt>
                <dd>{value}</dd>
              </div>
            )
          )}
        </dl>
      </div>
    );
  };

  return (
    <div className="account-metrics">
      {/* ---- blue banner — 1:9051, /account-metrics/accounts/<id>/ ---- */}
      {metrics.isLoading ? <BannerSkeleton /> : (
      <section className="am-banner">
        <span className="am-banner__avatar">
          <span className="icon am-banner__mark" style={icon('brand-mark')} />
        </span>
        <div className="am-banner__id">
          <span className="am-banner__caption">{t('Account metrics')}</span>
          <span className="am-banner__number">{loginId ? formatLoginId(loginId) : NA}</span>
          {bannerFacts.length > 0 && (
            <span className="am-banner__facts">
              {bannerFacts.map((fact) => (
                <span className="am-banner__fact" key={fact}>
                  {t(fact)}
                </span>
              ))}
            </span>
          )}
        </div>

        {/* The width the strip was wasting. Hidden on a phone, where the strip
            is already two rows tall and the same figures sit in the cards just
            below. */}
        <div className="am-banner__stats">
          {bannerStats.map((stat) => (
            <div className="am-banner__stat" key={stat.label}>
              <span className="am-banner__stat-label">{t(stat.label)}</span>
              <span
                className={`am-banner__stat-value${stat.tone ? ` is-${stat.tone}` : ''}`}
              >
                {stat.value}
              </span>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ---- loss limit alert — review doc, metrics: "Warn the trader before
          a limit is reached". Only while `triggered`; nothing otherwise. ---- */}
      {showLossAlert && (
        <p className="am-loss-alert" role="alert">
          {t("You have used {{pct}}% of today's loss limit. {{amount}} left before the account fails.", {
            pct: Number(lossAlert.used_pct ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 }),
            amount: usd(lossAlert.remaining, { decimals: 2 }),
          })}
        </p>
      )}

      {/* ---- why the account failed — under the banner, where the trader
          looks first: the rule, when, and the rule's article. ---- */}
      {showRisk && (
        <p className="am-failed" role="alert">
          <span className="am-failed__mark" aria-hidden="true">!</span>
          <span>
            {failedAt
              ? t('Account failed: {{reason}} on {{time}}.', { reason: t(breachReason), time: failedAt })
              : t('Account failed: {{reason}}.', { reason: t(breachReason) })}{' '}
            <a className="am-failed__link" href={failedRuleLink} target="_blank" rel="noreferrer">
              {t('Read the rule.')}
            </a>
          </span>
        </p>
      )}

      {/* ---- risk status — 1:9065, /account-metrics/accounts/<id>/ ----
          Only for a failed account with a breach on record (`showRisk`), and
          no skeleton while that is being decided: most accounts never show
          the bar, and a placeholder that then vanishes would only flicker. */}
      {showRisk && (
          <section className="am-risk am-risk--breach">
        {/* What the bar is about: the one hard breach on record, by name — not
            the risk status's "4 warnings", whose items ("Account disqualified",
            "Drawdown breached", …) are that breach's consequences rather than
            separate warnings. */}
        <div className="am-risk__left">
          <span className="am-risk__badge">{t('Risk status')}</span>
          <span className="am-risk__value">{t('Hard breach: {{reason}}', { reason: t(breachReason) })}</span>
        </div>
        <Button
          variant="pill"
          onClick={() =>
            navigate(loginId ? `/breach-analysis?login=${loginId}` : '/breach-analysis')
          }
        >
          {t('View all analytics')}
        </Button>
      </section>
      )}

      {mode === 'Advanced' && (
        <AccountAnalysis
          key={`${loginId}-${platform}`}
          loginId={loginId}
          platform={platform}
          account={account}
        />
      )}

      {/* ---- equity curve (/performance-chart/) + recent trades (/account-metrics/accounts/) — 1:9074 ---- */}
      <section className="am-row am-row--curve">
        {curve.isLoading ? <CurveSkeleton /> : (
        <div className="am-card am-curve">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('chart-profit')} />
              {t('Equity curve')}
            </span>
            <div className="am-curve__controls">
              <Button
                variant="toolbar"
                icon={<span className="icon" style={icon('Share')} />}
                onClick={() => setIsShareOpen(true)}
              >
                {t('Share')}
              </Button>
              {/* A free range, as live's chart has (N35). Today is the latest
                  day there can be data for. */}
              <DatePicker.RangePicker
                className="reusable-antd-datepicker am-curve__range"
                popupClassName="reusable-antd-datepicker-popup"
                value={customRange}
                onChange={(dates) => setCustomRange(dates?.[0] && dates?.[1] ? dates : null)}
                disabledDate={(day) => day && day.isAfter(new Date(), 'day')}
                format="DD MMM YYYY"
                allowClear
                placeholder={[t('Start date'), t('End date')]}
                aria-label={t('Equity curve date range')}
              />
              {/* No tab is lit while a custom range is showing. */}
              <SegmentedTabs
                radius="track"
                options={['1D', '1W', '1M', '3M', 'ALL']}
                value={customRange ? null : range}
                onChange={pickPreset}
              />
            </div>
          </header>

          <div className="am-curve__summary">
            {isCurveLoading ? (
              <>
                <Skeleton w="164px" h={34} />
                <Skeleton w="104px" h={13} />
              </>
            ) : (
              <>
                <span className="am-curve__value">{usd(latest, { decimals: 2 })}</span>
                <span className="am-curve__delta">
                  {change == null
                    ? NA
                    : [
                        signedUsd(change, { decimals: 2 }),
                        changePct == null
                          ? null
                          : `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                </span>
              </>
            )}
          </div>

          {curveNote ? (
            <div className="am-curve__empty">
              <span>{curveNote}</span>
              {curve.isError && (
                <Button variant="toolbar" onClick={curve.refetch}>
                  {t('Retry')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <LineChart
                className="am-curve__plot"
                data={points}
                // The newest point is the one the summary above is quoting.
                highlightedIndex={points.length - 1}
                // The reference card is 512px: 12 + 32 head + 72 figure band
                // + 384 plot + 12. When the levels' key shows under the plot
                // (10px gap + a 14px line), the plot gives up those 24px so
                // the card stays 512px either way.
                height={curveLevels.length > 0 ? 360 : 384}
                // A faint white guide, as the reference draws it (its
                // strength is set in AccountMetrics.scss).
                guideColor="#fff"
                loading={isCurveLoading}
                referenceLines={curveLevels}
              />
              {curveLevels.length > 0 && (
                <ChartLegend
                  className="am-curve__legend"
                  items={[
                    { label: t('Equity'), color: '#1A5DFD' },
                    ...curveLevels.map(({ label, color }) => ({ label, color })),
                  ]}
                />
              )}
            </>
          )}
        </div>
        )}

        {metrics.isLoading ? <TradesSkeleton /> : (
        <div className="am-card am-trades">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('table-accounts')} />
              {t('Recent trades')}
            </span>
            {/* The list above is the newest six; the whole ledger lives on Trade
                History — AC1-I106. That page reads the same `?login&platform`
                deep link this one does, so the account carries across rather
                than the trader landing on whichever account is first. */}
            <Button variant="pill" onClick={() => navigate(tradeHistoryHref)}>
              {t('View all')}
            </Button>
          </header>
          <ul className="am-trades__list">
            {/* `position_id` repeats on a partial close, so the index keeps keys unique. */}
            {trades.slice(0, 6).map((row, i) => (
              <li className="am-trades__item" key={`${row.position_id}-${i}`}>
                <SymbolFlag symbol={row.symbol.split('.')[0]} />
                <div className="am-trades__id">
                  <span className="am-trades__symbol">{row.symbol}</span>
                  <span className="am-trades__meta">
                    {t(row.side)} · {t('{{volume}} lots', { volume: row.volume })} · {tradeWhen(row.close_time)}
                  </span>
                </div>
                <span className={`am-trades__result is-${row.net_profit < 0 ? 'down' : 'up'}`}>
                  {signedUsd(row.net_profit, { decimals: 2 })}
                </span>
              </li>
            ))}
            {trades.length === 0 && (
              <li className="am-trades__empty">
                {metrics.isFetching ? t('Loading…') : t('No closed trades on this account.')}
              </li>
            )}
          </ul>
          {/* The account's total from its statistics (F21) — `recent_trades`
              is only the latest batch, so its length is not the total. */}
          <footer className="am-trades__foot">
            {num(metrics.data?.statistics?.total_trades) != null
              ? num(metrics.data.statistics.total_trades) === 1
                ? t('{{count}} closed trade', { count: 1 })
                : t('{{count}} closed trades', { count: num(metrics.data.statistics.total_trades) })
              : t('Last {{count}} closed trades', { count: trades.length })}
          </footer>
        </div>
        )}
      </section>

      {/* ---- trading objectives — 1:9268, /objectives-flat/<id>/ ---- */}
      {isObjectivesLoading ? (
        <ObjectivesSkeleton />
      ) : objectives.isError || !obj ? (
        <section className="am-card am-objectives">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('metrics-objectives')} />
              {t('Trading objectives')}
            </span>
          </header>
          <div
            className="am-curve__empty"
            style={{
              padding: '32px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span>
              {!loginId
                ? t('Select an account to view trading objectives.')
                : t('Could not load trading objectives for this account.')}
            </span>
            {loginId && (
              <Button variant="toolbar" onClick={() => objectives.refetch()}>
                {t('Retry')}
              </Button>
            )}
          </div>
        </section>
      ) : (
      <section className="am-card am-objectives">
        <header className="am-card__head">
          <span className="am-card__title">
            <span className="icon am-card__icon" style={icon('metrics-objectives')} />
            {t('Trading objectives')}
          </span>
          <div className="am-objectives__state">
            <StatusChip>{statusText(obj?.overall_status ?? account?.progress)}</StatusChip>
            {(isCompetition || hasTarget) && (
              <span className="am-objectives__note">
                {isCompetition
                  ? t('Competition account — ranked on its result, no profit target')
                  : t('{{amount}} to target', { amount: usd(profitLeft) })}
              </span>
            )}
          </div>
        </header>

        {/* One grid for the whole card: the profit target and the two loss
            limits across the top, the account-wide rules on the row under
            them — so every block shares the same column edges, and the target
            is only as tall as the limits beside it. */}
        <div className={`am-objectives__body${hasTarget ? '' : ' am-objectives__body--no-target'}`}>
          {/* The profit-target ring — only where there is a target. */}
          {hasTarget && (
          <div className="am-objectives__target">
            <div
              className="am-ring"
              role="img"
              aria-label={t('{{pct}}% passed', { pct: passedPct == null ? 0 : Math.round(passedPct) })}
            >
              <svg width="88" height="88" viewBox="0 0 88 88" className="am-ring__svg">
                <defs>
                  <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1A5DFD" />
                    <stop offset="100%" stopColor="#103897" />
                  </linearGradient>
                </defs>
                <circle
                  cx="44"
                  cy="44"
                  r="40"
                  fill="transparent"
                  stroke="rgba(255, 255, 255, 0.07)"
                  strokeWidth="8"
                />
                <circle
                  cx="44"
                  cy="44"
                  r="40"
                  fill="transparent"
                  stroke="url(#ring-grad)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray="251.327"
                  strokeDashoffset={251.327 * (1 - (passedPct ?? 0) / 100)}
                  transform="rotate(-90 44 44)"
                />
              </svg>
              <div className="am-ring__content">
                <span className="am-ring__pct">
                  {passedPct == null ? NA : `${Math.round(passedPct)}%`}
                </span>
                <span className="am-ring__label">{t('passed')}</span>
              </div>
            </div>
            <div className="am-objectives__figures">
              <span className="am-objectives__caption">{t('Profit target')}</span>
              <p className="am-objectives__amount">
                {profitLeft == null ? NA : usd(profitLeft, { decimals: 2 })}
                <span className="am-objectives__amount-note">{t('left to pass')}</span>
              </p>
              <span className="am-objectives__caption">
                {profitMade == null || !profitTarget
                  ? NA
                  : t('{{earned}} earned of {{target}}', {
                      earned: usd(profitMade, { decimals: 2 }),
                      target: usd(profitTarget, { decimals: 2 }),
                    })}
              </span>
            </div>
          </div>
          )}

            {/* Simple: the target, the daily loss and the risk rule on the first
                row; the overall loss, consistency and inactivity on the second.
                Advanced has no risk-rule card, so both loss limits stay up top. */}
            {(mode === 'Simple' ? limits.filter((l) => l.key !== 'overall') : limits).map(renderLimit)}

            {mode === 'Simple' && (
            <div
              className={`am-limit am-limit--rule${accountFailed ? ' am-limit--breached' : ''}`}
            >
              <div className="am-limit__head">
                <span className="am-limit__label">
                  {t('Max risk percent')}
                  {/* The rule's own status, as every other objective carries
                      one (N110). */}
                  {riskRule.status && <StatusChip>{riskRule.status}</StatusChip>}
                  {/* <a
                    className="am-limit__help"
                    href={RISK_RULE_HELP}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="How the max risk rule works"
                    title="How the max risk rule works"
                  >
                    ?
                  </a> */}
                </span>
                <span className="am-limit__left">{t('{{limit}} per asset', { limit: riskRule.label })}</span>
              </div>
              {/* No rail: nothing reports how much of the per-asset allowance
                  has been used while an account is still trading, and an empty
                  rail would read as "none used" rather than "not measured". The
                  spacer only keeps its note level with the daily loss's beside it. */}
              <span className="am-limit__rail am-limit__rail--spacer" aria-hidden="true" />
              <div className="am-limit__notes">
                <span className="am-limit__note">
                  {riskRule.limitParts ? (
                    <>
                      {t(riskRule.limitParts.lead)}{' '}
                      <span className="am-limit__figure">
                        {riskRule.limitParts.figure}
                      </span>{' '}
                      {t(riskRule.limitParts.tail)}
                    </>
                  ) : (
                    t(riskRule.note)
                  )}
                </span>
                {/* The rule in general, under this account's own figure. The
                    threshold is set per account size, so a trader reading 1%
                    here cannot tell whether that is the rule or just theirs —
                    and the ones who ask are usually comparing accounts. */}
                {riskRule.breachNote ? (
                  <span className="am-limit__note">
                    {t('Loss at breach: {{figures}}', { figures: riskRule.breachNote })}
                  </span>
                ) : (
                  <span className="am-limit__note am-limit__note--info">
                    {t('It can be 1% to 3% per asset, depending on the account.')}
                  </span>
                )}
              </div>
            </div>
            )}

            {mode === 'Simple' && <div className="am-objectives__divider" aria-hidden="true" />}

            {/* Second row (Simple): the overall loss, consistency, inactivity.
                Advanced carries consistency in the details grid below. */}
            {mode === 'Simple' && renderLimit(limits.find((l) => l.key === 'overall'))}

            {mode === 'Simple' && consistency && (
              <div
                className={`am-limit${
                  accountFailed
                    ? ' am-limit--breached'
                    : consistency.share > consistency.cap
                      ? ' am-limit--over'
                      : ''
                }`}
              >
                <div className="am-limit__head">
                  <span className="am-limit__label">{t('Consistency')}</span>
                  <span className="am-limit__left">
                    {consistency.share == null
                      ? NA
                      : t('{{share}} of {{cap}} max', {
                          share: percent(consistency.share),
                          cap: percent(consistency.cap),
                        })}
                  </span>
                </div>
                {/* Already a percentage of the profit, so it fills the rail as-is. */}
                <span className="am-limit__rail">
                  <span
                    className="am-limit__fill"
                    style={{ width: `${Math.max(0, Math.min(consistency.share ?? 0, 100))}%` }}
                  />
                </span>
                <dl className="am-limit__notes">
                  <div className="am-limit__row">
                    <dt>{t('Best day')}</dt>
                    <dd>
                      {consistency.bestDay == null
                        ? t('None yet')
                        : [
                            usd(consistency.bestDay, { decimals: 2 }),
                            consistency.bestDayOn && asDate(consistency.bestDayOn),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Inactivity, counted down — after the reference in the review doc:
                "4 / 30", how long is left to trade, and a bar that ends in the
                rule's state (on track, breached, or not applying here). */}
            {mode === 'Simple' && inactivityRule && (
              <div
                className={`am-limit am-inactivity${
                  inactivityRule.notApplicable
                    ? ' am-inactivity--off'
                    : inactivityRule.breached
                      ? ' am-limit--breached'
                      : ''
                }`}
              >
                <div className="am-limit__head">
                  <span className="am-limit__label">
                    {t('Inactivity')}
                    <InfoTip
                      className="am-limit__help"
                      label={t('How the inactivity rule works')}
                      title={t('Inactivity rule')}
                    >
                      <span>
                        {t('Place at least one trade every {{days}} days to keep the account active.', {
                          days: inactivityRule.limit,
                        })}
                      </span>
                      <InfoTip.Row label={t('Days without a trade')}>
                        {t('{{used}} of {{allowed}}', { used: inactivityRule.used, allowed: inactivityRule.limit })}
                      </InfoTip.Row>
                      <InfoTip.Row label={t('Trade by')}>{asDate(inactivityRule.deadline)}</InfoTip.Row>
                      <InfoTip.Row label={t('Last trade')}>
                        {inactivityRule.lastTrade ? asBrokerTime(inactivityRule.lastTrade) : t('No trades yet')}
                      </InfoTip.Row>
                    </InfoTip>
                  </span>
                  {inactivityRule.status && <StatusChip>{statusText(inactivityRule.status)}</StatusChip>}
                </div>

                {inactivityRule.notApplicable ? (
                  <span className="am-inactivity__hint">
                    {t("This rule doesn't apply to the account now.")}
                  </span>
                ) : (
                <>
                <div className="am-inactivity__count">
                  {inactivityRule.used} / {inactivityRule.limit}
                </div>
                <span className="am-inactivity__hint">
                  {inactivityRule.breached
                    ? t('No trade within the limit')
                    : (inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used) === 1
                      ? t('Trade within {{count}} day', {
                          count: inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used,
                        })
                      : t('Trade within {{count}} days', {
                          count: inactivityRule.remaining ?? inactivityRule.limit - inactivityRule.used,
                        })}
                </span>

                <div className="am-inactivity__track">
                  <span className="am-limit__rail">
                    <span
                      className="am-limit__fill"
                      style={{
                        width: `${Math.min((inactivityRule.used / inactivityRule.limit) * 100, 100)}%`,
                      }}
                    />
                  </span>
                  <span className="am-inactivity__mark" aria-hidden="true">
                    <span
                      className="icon"
                      style={icon(inactivityRule.breached ? 'warning-triangle' : 'check-sm')}
                    />
                  </span>
                </div>
                </>
                )}
              </div>
            )}

        </div>

        {mode === 'Advanced' && (
          <>
            <div className="am-card__rule" />
            <div className="am-objectives__details">
              {objectiveDetails.map((d) => (
                <div className="am-metric" key={d.label}>
                  <span className="am-metric__label">{t(d.label)}</span>
                  <span className="am-metric__value">{d.value}</span>
                  <span className="am-metric__note">{d.note}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
      )}

      {/* ---- statistics (/account-metrics/accounts/) + daily performance — 1:9320 ---- */}
      {/* Advanced keeps the design's own split (the calendar narrower); the
          wider calendar is Simple's — see `.am-row--stats` in the scss. */}
      <section className={`am-row am-row--stats${mode === 'Advanced' ? ' am-row--stats-advanced' : ''}`}>
        {/* Advanced swaps the twelve cells for the full ledger — 1:10443. */}
        {metrics.isLoading ? <StatsSkeleton /> : mode === 'Advanced' ? (
        <AdvancedStatistics
          data={metrics.data}
          stage={stage}
          // A Simple/Advanced switch is a new request; a background refresh
          // is not, and must not blank the ledger.
          loading={metrics.isFetching && !metrics.currentData}
          highWaterMark={highWaterMark}
          definitions={statisticsDefinitions}
        />
        ) : (
        <div className="am-card am-stats">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('bar-icon')} />
              {t('Statistics')}
            </span>
            <span className="am-card__meta">
              {statistics?.total_trades == null
                ? NA
                : t('{{count}} trades', { count: statistics.total_trades })}{' '}
              · {t(mode)}
            </span>
          </header>
          {!statistics ? (
            <p className="am-stats__empty">
              {metrics.isFetching ? t('Loading…') : t('No statistics came back for this account.')}
            </p>
          ) : (
            <dl className={`am-stats__grid${highWaterMark != null ? ' am-stats__grid--extra' : ''}`}>
              {STATS.map((stat) => {
                const value = stat.get ? stat.get(statistics) : statistics[stat.key];
                const known = value != null;
                const tone = stat.signed && known && value !== 0 ? (value < 0 ? 'down' : 'up') : null;
                return (
                  <div className="am-stats__cell" key={stat.key}>
                    <dt className="am-stats__label">
                      <StatLabel
                        label={stat.label}
                        // The one cell that shows two figures explains both.
                        def={stat.key === 'wins' ? ['wins', 'losses'] : stat.key}
                        definitions={statDefs}
                      />
                    </dt>
                    <dd className={`am-stats__value${tone ? ` is-${tone}` : ''}`}>
                      {known ? stat.format(value) : NA}
                    </dd>
                  </div>
                );
              })}
              {/* Trailing accounts only — the balance the limit trails (N111). */}
              {highWaterMark != null && (
                <div className="am-stats__cell">
                  <dt className="am-stats__label">{t('High-water mark')}</dt>
                  <dd className="am-stats__value">{money(highWaterMark)}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
        )}

        {/* the date-fns calendar */}
        {/* Opens on the month the trades are in, and on the latest trading day
            — the calendar is useless pointing at a month the account didn't
            trade, which is what "today" usually is on a finished account.
            Which is also why it waits: `key` remounts it when `tradeMonth`
            lands, so rendering it early would show an empty current month and
            then jump to another one. */}
        {metrics.isLoading ? <DailySkeleton /> : (
        <DailyPerformance
          key={tradeMonth ? tradeMonth.toISOString() : 'empty'}
          days={dayStats}
          trades={trades}
          initialMonth={tradeMonth ?? new Date()}
          initialSelected={tradeMonth}
          // Advanced's statistics ledger is the taller card: the calendar
          // takes its height, and a busy day's trades fill it. Simple keeps
          // the Day view to the calendar's own height.
          fill={mode === 'Advanced'}
        />
        )}
      </section>

      {/* ---- account information — 1:9535, /account-metrics/accounts/<id>/ ---- */}
      {metrics.isLoading ? <InfoSkeleton /> : (
      <section className="am-info">
        <header className="am-card__head">
          <span className="am-card__title">
            <span className="icon am-card__icon" style={icon('metrics-account')} />
            {t('Account information')}
          </span>
          <span className="am-card__meta">
            {accountName || NA} · {formatLoginId(account?.login_id)}
          </span>
        </header>

        <div className="am-info__row">
          <Field
            label="Login ID"
            value={formatLoginId(account?.login_id)}
            copy={account?.login_id}
          />
          {/* No investor password: we don't offer one, so it isn't shown even
              when the account record carries a value for it. */}
          <Field
            label="Master password"
            value={NA}
            secret={account?.password}
            copy={account?.password}
            mono
          />
          <Field
            label="Platform"
            value={account?.platform ? (PLATFORM_LABELS[account.platform] ?? account.platform) : NA}
          />
          <Field label="Server" value={challenge?.server ?? NA} copy={challenge?.server} />
        </div>


        <div className="am-info__row">
          <Field label="Start date" value={asDate(account?.start_date)} />
          <Field label="Plan type" value={t(challenge?.plan_type) ?? NA} />
          <Field label="Stage" value={stageLabel} />
          <Field label="Account size" value={formatAccountSize(challenge?.account_balance)} />
          <div className="am-field">
            <span className="am-field__label">{t('Status')}</span>
            <div className="am-field__row">
              {accountStatus !== NA ? (
                <StatusChip>{accountStatus}</StatusChip>
              ) : (
                <span>{NA}</span>
              )}
            </div>
          </div>
        </div>

        {/* N110 — what the live Account Metric shows beside these: the money
            now, the day's rollover, the account's terms, and when it ends. */}
        <div className="am-info__row">
          <Field label="Balance" value={money(flashcard?.balance)} />
          <Field label="Equity" value={money(flashcard?.equity)} />
          <Field label="Rollover balance" value={money(rollover?.balance)} />
          <Field label="Rollover equity" value={money(rollover?.equity)} />
          <Field label="Interest (swap)" value={swapPaid == null ? NA : money(swapPaid)} />
        </div>

        <div className="am-info__row">
          <Field
            label="Leverage"
            value={challenge?.Leverage ? `1:${challenge.Leverage}` : NA}
          />
          <Field
            label="Type"
            value={t(readableType(account?.type || flashcard?.type))}
          />
          <Field label="Expiry date" value={asDate(account?.expiry_date)} />
          <Field
            label="Last updated"
            value={
              metrics.fulfilledTimeStamp
                ? format(metrics.fulfilledTimeStamp, 'dd MMM yyyy, HH:mm:ss')
                : NA
            }
          />
        </div>

        {/* Only on an account that breached — the snapshot taken at the breach. */}
        {breachedAt && (
          <div className="am-info__row">
            <Field label="Breached at" value={asBrokerTime(breachedAt.time)} />
            <Field label="Balance at breach" value={money(breachedAt.balance)} />
            <Field label="Equity at breach" value={money(breachedAt.equity)} />
          </div>
        )}
      </section>
      )}

      {/* Advanced adds three sections below Account information — 1:10931. */}
      {mode === 'Advanced' && (
        <AdvancedSections data={metrics.data} loading={metrics.isFetching} />
      )}

      {/* Page level, not inside the curve card — that card is swapped for its
          skeleton whenever the range changes, which would close the sheet. */}
      <ShareDialog
        open={isShareOpen}
        accountId={loginId}
        account={accounts?.find((a) => a.login_id === loginId)}
        onClose={() => setIsShareOpen(false)}
      />
    </div>
  );
}
